/**
 * NotificationItem — Single notification row with icon, title, message, timestamp, read status.
 *
 * @example
 * <NotificationItem
 *   notification={notif}
 *   onMarkRead={() => markAsRead(notif.id)}
 *   onDelete={() => deleteNotif(notif.id)}
 * />
 */

import { clsx } from 'clsx';
import {
  Bell,
  Target,
  GraduationCap,
  Share2,
  Info,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  X,
} from 'lucide-react';
import type { AppNotification } from '@/lib/api/types';

export interface NotificationItemProps {
  notification: AppNotification;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'goal':
      return { icon: Target, bg: 'bg-primary-50', text: 'text-primary-600' };
    case 'study':
      return { icon: GraduationCap, bg: 'bg-success-50', text: 'text-success-600' };
    case 'sharing':
      return { icon: Share2, bg: 'bg-accent-100', text: 'text-accent-700' };
    case 'warning':
      return { icon: AlertTriangle, bg: 'bg-warning-50', text: 'text-warning-600' };
    case 'success':
      return { icon: CheckCircle2, bg: 'bg-success-50', text: 'text-success-600' };
    case 'info':
    default:
      return { icon: Info, bg: 'bg-blue-50', text: 'text-blue-600' };
  }
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function NotificationItem({ notification, onMarkRead, onDelete }: NotificationItemProps) {
  const { icon: Icon, bg, text } = getNotificationIcon(notification.type);

  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-3 border-b border-gray-50 last:border-0 transition-colors hover:bg-gray-50',
        !notification.isRead && 'bg-primary-50/30',
      )}
    >
      {/* Icon */}
      <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', bg, text)}>
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-medium text-gray-900 truncate">{notification.title}</p>
          {!notification.isRead && (
            <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" aria-label="Unread" />
          )}
        </div>
        <p className="text-xs text-gray-500 line-clamp-2">{notification.message}</p>
        <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notification.createdAt)}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!notification.isRead && onMarkRead && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Mark as read"
            title="Mark as read"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(notification.id)}
            className="p-1 rounded text-gray-400 hover:bg-danger-50 hover:text-danger-600 transition-colors"
            aria-label="Delete notification"
            title="Delete"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default NotificationItem;